<template>
  <v-card v-if="loading">
    <v-skeleton-loader type="image" />
  </v-card>

  <v-card v-else flat class="card-gauge d-flex justify-center" data-cy="stats-card">
    <v-card-text>
      <div v-if="isEmpty">
        <p>{{ $t('honda.dashboard.stats.no-data') }}</p>
      </div>

      <div v-else>
        <span v-if="status === 'on_track'" class="card-goal on-track">
          <v-icon>fa-light fa-check</v-icon>
        </span>

        <span v-if="status === 'needs_push'" class="card-goal needs-push">
          <v-icon>fa-light fa-triangle-exclamation</v-icon>
        </span>

        <div class="card-gauge-metric">
          <p :class="`metric-title ${status ? 'max-width-90' : ''}`">
            <span>{{ title }}</span>
            <span v-if="subtitle" class="metric-subtitle">{{ subtitle }}</span>
          </p>
          <p class="metric-value">{{ current }}</p>
        </div>

        <div class="card-gauge-metrics">
          <v-chart class="chart-render" ref="chart" :option="options" :autoresize="true" />
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import {computed, defineComponent, toRefs} from '@vue/composition-api'

export default defineComponent({
  name: 'CardGauge',
  props: {
    loading: {
      type: Boolean,
      required: false,
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: false,
    },
    current: {
      type: Number,
      required: false,
    },
    percent: {
      type: Number,
      required: false,
    },
    target: {
      type: Number,
      required: false,
      default: 0,
    },
    status: {
      type: String,
      required: false,
    },
    semi: {
      type: Boolean,
      required: false,
    },
  },
  setup(props) {
    const {current, percent, target} = toRefs(props)

    const config = {
      angleStart: 180,
      angleEnd: 0,
      fontColor: '#000',
      fontFamily: 'ProximaNovaRegular',
      lineWidth: 23,
      position: ['50%', '60%'],
    }

    const isEmpty = computed(() => {
      return !current?.value && current?.value !== 0 && !percent?.value && percent?.value !== 0
    })

    const targetDegrees = computed(() => {
      return (target.value / 400) * -360 + config.angleStart
    })

    const options = computed(() => {
      return {
        series: [
          {
            type: 'gauge',
            center: config.position,
            startAngle: config.angleStart,
            endAngle: config.angleEnd,
            silent: true,
            progress: {
              show: true,
              width: config.lineWidth,
              itemStyle: {
                color: '#dc192d',
              },
            },
            axisLine: {
              lineStyle: {
                width: config.lineWidth,
                color: [[1, '#eee']],
              },
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              show: false,
            },
            splitLine: {
              show: false,
            },
            pointer: {
              show: false,
            },
            detail: {
              fontSize: 18,
              fontFamily: config.fontFamily,
              formatter: `${percent?.value}%`,
              color: config.fontColor,
              offsetCenter: [0, 0],
            },
            data: [{value: percent?.value ? percent?.value / 2 : 0}],
          },
          {
            type: 'gauge',
            center: config.position,
            splitNumber: 1,
            z: 3,
            startAngle: config.angleStart,
            endAngle: targetDegrees.value,
            min: 0,
            max: target.value,
            axisLine: {
              lineStyle: {
                width: 0,
              },
            },
            splitLine: {
              distance: 0,
              length: config.lineWidth,
              lineStyle: {
                width: 1,
                color: config.fontColor,
              },
            },
            axisLabel: {
              formatter: (value: number) => (value === 0 ? '' : `${value}%`),
              distance: -45,
              color: config.fontColor,
            },
            axisTick: {
              show: false,
            },
            pointer: {
              show: false,
            },
          },
        ],
      }
    })

    return {
      isEmpty,
      options,
    }
  },
})
</script>

<style>
.v-skeleton-loader__image {
  height: 270px;
}

.max-width-90 {
  max-width: 90%;
}
</style>

<style lang="scss" scoped>
.card-gauge {
  width: 100%;
  height: 270px;
  border-radius: 6px !important;
  box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.16) !important;
  font-family: 'Roboto', serif;
  overflow: hidden;
}

.card-goal {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 2;
  width: 40px;
  height: 40px;
  border-radius: 20px;

  &.on-track {
    background-color: #00803f;
  }

  &.needs-push {
    background-color: #fca400;
  }

  > i {
    color: white;
    font-size: 17px;
    padding-top: 14px;
    padding-left: 9px;
  }
}

.metric-title {
  color: #222;
  font-family: 'ProximaNovaExCnSBold', system-ui, sans-serif;
  font-size: 20px;
  line-height: 1;
  text-transform: uppercase;
  margin-bottom: 2px;
}

.metric-subtitle {
  color: #aaa;
  font-family: 'ProximaNovaRegular', system-ui, sans-serif;
  font-size: 14px;
  margin-left: 5px;
  text-transform: none;
}

.metric-value {
  display: block;
  color: #222;
  font-family: 'ProximaNovaSemibold', system-ui, sans-serif;
  font-size: 32px;
  line-height: 1;
  text-transform: uppercase;
  margin-bottom: 5px;
  min-height: 32px;
}

.card-gauge-metrics {
  color: #888888;
  font-family: 'ProximaNovaRegular', system-ui, sans-serif;
  font-size: 14px;
  width: 100%;
  height: 168px;
}
</style>
